import { create } from "zustand";
import type { ChatRole, NikolaMessagePart } from "@chessla/shared";
import type { MarkerWarning, MessageSegment } from "../board/parse-board-markers";
import type { BoardArrow, BoardHighlight } from "@/shared/stores/board.store";
import type { PendingAction } from "./chat.api";

/** Board state attached to a specific message — renders as an inline chessboard within that message's bubble. */
export interface MessageBoardState {
  fen: string;
  /** When set, the frontend resolves the real FEN from analysis data instead of trusting the fen field. */
  positionRef?: { gameId: string; moveNumber: number; ply?: number | null } | null;
  orientation: "white" | "black";
  arrows: BoardArrow[];
  highlights: BoardHighlight[];
  gameId: string | null;
  plyRange: { start: number; end: number } | null;
  compare: { playedFrom: string; playedTo: string; bestFrom: string; bestTo: string } | null;
  evalLineId: string | null;
}

export interface ChatStoreMessage {
  role: ChatRole;
  content: string;
  parts?: readonly NikolaMessagePart[];
  createdAt?: string;
  /** Ordered segments: text chunks and boards interleaved in the order they appeared. Used for rendering. */
  segments?: MessageSegment[];
  /** Board markers rejected in this message's reply, if any — see CorrectionNotice.tsx. Always empty for user messages. */
  warnings?: MarkerWarning[];
  /** All board states (flat list, for global store / backward compat). */
  boards?: MessageBoardState[];
  /**
   * Set when this message is the one that surfaced a frontend action —
   * renders an inline "Start Analysis" affordance (and, once started,
   * the live progress panel) directly under this bubble. See
   * ChatPage.tsx's pendingAction handling and analysis-runner.ts.
   * Cleared (set to null) once the player starts the analysis, so the
   * button doesn't linger after it's been actioned.
   */
  pendingAction?: PendingAction | null;
  creditsCharged?: number;
  failedDraft?: string;
}

interface ChatState {
  playerId: string | null;
  sessionId: string | null;
  messages: ChatStoreMessage[];
  setSession: (playerId: string, sessionId: string) => void;
  appendMessage: (message: ChatStoreMessage) => void;
  /** Replaces the whole message list — used when resuming a session's history from the server on page load, as opposed to appendMessage's incremental use during an active chat. */
  setMessages: (messages: ChatStoreMessage[]) => void;
  /** Patches the most recently appended message in place — used to clear a message's pendingAction once the player starts analysis from it, without touching any other message. */
  updateLastMessage: (update: Partial<ChatStoreMessage>) => void;
  updateMessage: (index: number, update: Partial<ChatStoreMessage>) => void;
}

export const useChatStore = create<ChatState>((set) => ({
  playerId: null,
  sessionId: null,
  messages: [],
  setSession: (playerId, sessionId) => set({ playerId, sessionId }),
  appendMessage: (message) =>
    set((state) => ({
      messages: [...state.messages, { ...message, createdAt: message.createdAt ?? new Date().toISOString() }],
    })),
  setMessages: (messages) => set({ messages }),
  updateLastMessage: (update) =>
    set((state) => {
      if (state.messages.length === 0) return state;
      const messages = state.messages.slice();
      messages[messages.length - 1] = { ...messages[messages.length - 1], ...update };
      return { messages };
    }),
  updateMessage: (index, update) =>
    set((state) => {
      if (!state.messages[index]) return state;
      const messages = state.messages.slice();
      messages[index] = { ...messages[index], ...update };
      return { messages };
    }),
}));
